import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Network, ExternalLink } from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import Certifications from '@/components/Certifications';
import VideoObjectSchema from '@/components/VideoObjectSchema';
import { Button } from '@/components/ui/button';
import { openCalendlyDemo } from '@/lib/analytics';
import { FOUNDER } from '@/lib/founder';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About EZ Cloud — Founder-led Oracle AP Automation | EZ Cloud"
        description="EZ Cloud is founder-led by Andrew Blackman, a 25-year Oracle accounts-payable specialist and Oracle-certified AI professional. Meet the Oracle AP expertise behind the platform."
        canonical="https://ezcloud.ai/about"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'About', url: 'https://ezcloud.ai/about' },
        ]}
      />
      <VideoObjectSchema />
      <Navigation />
      <Breadcrumbs />

      <main className="container mx-auto px-4 py-16 max-w-4xl">
        <header className="mb-12 max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-accent mb-3">
            Founder-led
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5">
            The Oracle AP expertise behind EZ Cloud
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            Software is getting easier to build. Invoice capture is now a commodity — anyone can
            extract data from a PDF. What you can't shortcut is knowing how Oracle accounts payable
            actually runs, and building automation that respects it. That knowledge is the whole
            point of EZ Cloud.
          </p>
        </header>

        {/* Founder intro */}
        <section className="flex flex-col sm:flex-row items-start gap-6 mb-14 p-8 rounded-2xl bg-gray-50 border border-gray-200">
          <img
            src={FOUNDER.photo}
            alt={FOUNDER.photoAlt}
            width={80}
            height={80}
            className="flex-shrink-0 w-20 h-20 rounded-full object-cover border border-gray-200"
          />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{FOUNDER.name}</h2>
            <p className="text-accent font-semibold mb-3">
              {FOUNDER.role}, {FOUNDER.company}
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">{FOUNDER.bio}</p>
            <a
              href={FOUNDER.credlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent transition-colors"
            >
              View verified credentials on Credly
              <ExternalLink className="w-3.5 h-3.5" aria-hidden />
            </a>
          </div>
        </section>

        {/* Why founder-led matters */}
        <section className="mb-14 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Why founder-led matters for Oracle AP
          </h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Every enterprise AP team knows the movie: a brilliant expert runs the sales demo, the
              contract is signed, and then you're handed to a delivery team that has never seen your
              Oracle environment — and you spend six months re-explaining your own business.
            </p>
            <p>
              EZ Cloud is the opposite. The person who has lived inside Oracle AP for 25 years is the
              same person on your first call and at your go-live. No context lost in handoff, no
              junior team learning Oracle on your time. For a high-stakes Oracle Fusion or EBS
              implementation, that direct access to the expertise <em>is</em> the product.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            <Link
              to="/integrations/oracle"
              className="group flex items-start gap-3 p-5 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-accent/50 hover:shadow-md transition-all"
            >
              <Network className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
              <div>
                <div className="font-semibold text-gray-900 mb-1">Published on Oracle's architecture</div>
                <p className="text-sm text-gray-600">
                  EZ Cloud is on the Oracle Reference Architecture for OCI and featured in Oracle's
                  technical case studies.
                </p>
              </div>
            </Link>
            <Link
              to="/conferences"
              className="group flex items-start gap-3 p-5 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-accent/50 hover:shadow-md transition-all"
            >
              <ShieldCheck className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
              <div>
                <div className="font-semibold text-gray-900 mb-1">In the room, not just online</div>
                <p className="text-sm text-gray-600">
                  We meet Oracle and public-sector finance leaders at Oracle AI World, IOFM, and CGCS.
                  See where we'll be.
                </p>
              </div>
            </Link>
          </div>
        </section>

        {/* Oracle-published proof video */}
        <section className="mb-14">
          <p className="text-sm font-semibold uppercase tracking-wider text-accent mb-3">
            Featured by Oracle Cloud Infrastructure
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Oracle's own customer story on EZ Cloud
          </h2>
          <p className="text-gray-700 leading-relaxed max-w-3xl mb-6">
            Oracle Cloud Infrastructure published this customer story on their official channel:
            the EZ Cloud accounts payable platform running on OCI — a Kubernetes cluster with an
            Autonomous Database and API Gateway, integrating with multiple ERP applications and
            scaling on demand at a fraction of the cost of running on VMs.
          </p>
          <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-gray-200 shadow-sm" style={{ aspectRatio: '16 / 9' }}>
            <iframe
              className="absolute inset-0 h-full w-full"
              src="https://www.youtube.com/embed/4QuE_v6HBPA"
              title="EZ Cloud transitions to serverless for accounts payable automation — Oracle Cloud Infrastructure"
              loading="lazy"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
          <a
            href="https://www.youtube.com/watch?v=4QuE_v6HBPA"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-primary hover:text-accent transition-colors"
          >
            Watch on Oracle Cloud Infrastructure's channel
            <ExternalLink className="w-3.5 h-3.5" aria-hidden />
          </a>
        </section>

        {/* Certifications */}
        <section className="mb-14">
          <Certifications />
        </section>

        {/* CTA */}
        <section className="text-center border-t border-gray-200 pt-12">
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-3">
            Talk to the person who built it
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Book a 30-minute walkthrough and we'll run a real exception from supplier email to Oracle
            posting — against your Fusion or EBS environment.
          </p>
          <Button size="lg" variant="accent" onClick={() => openCalendlyDemo()}>
            Book an Oracle AP demo
            <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
          </Button>
        </section>
      </main>
    </div>
  );
};

export default About;
